/**
 * useEnsureObjectsLoaded — Hook that reactively returns database objects,
 * triggering an on-demand fetch if they aren't cached yet.
 */

import { useEffect, useCallback, useState } from 'react';
import type { DatabaseEngineId } from '@/core/engines';
import type { DatabaseObject } from '../models/databaseObjectTypes';
import { useDatabaseStore } from '../store/databaseStore';
import { loadObjects, type ObjectsLoadResult } from '../services/ensureObjectsLoaded';

export type EnsuredObjects = {
  /** The complete object list, or `undefined` while loading or after a failed load. */
  readonly objects: DatabaseObject[] | undefined;
  /**
   * Set when the last load of this connection + database failed (some or all
   * type listings). Without it a failed load is indistinguishable from one
   * still running — `objects` stays `undefined` either way.
   */
  readonly failure: ObjectsLoadResult | undefined;
  /** Load again after a failure. */
  readonly retry: () => void;
};

/**
 * Returns preloaded database objects for the given connection+database.
 * If objects are not yet loaded, triggers an on-demand fetch in the background.
 * The return value updates reactively once the fetch completes.
 */
export function useEnsureObjectsLoaded(
  connectionId: string | undefined,
  database: string | undefined,
  engine?: DatabaseEngineId | string,
): EnsuredObjects {
  const objects = useDatabaseStore(
    useCallback(
      (s) => connectionId && database ? s.getPreloadedObjects(connectionId, database) : undefined,
      [connectionId, database],
    ),
  );
  const pairKey = connectionId && database ? `${connectionId}:${database}` : undefined;
  const [failed, setFailed] = useState<{ key: string; result: ObjectsLoadResult } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!connectionId || !database || !pairKey) return undefined;
    if (objects !== undefined) return undefined;

    // The store update re-renders on success; a failure is kept here.
    let cancelled = false;
    void loadObjects(connectionId, database, engine).then((result) => {
      if (!cancelled && result.failures.length > 0) setFailed({ key: pairKey, result });
    });
    return () => {
      cancelled = true;
    };
  }, [connectionId, database, pairKey, engine, objects, attempt]);

  const retry = useCallback(() => {
    setFailed(null);
    setAttempt((n) => n + 1);
  }, []);

  const failure = objects === undefined && failed && failed.key === pairKey ? failed.result : undefined;
  return { objects, failure, retry };
}
